import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'
import { isClerkEnabled } from '../../config/clerkConfig'
import { isAuthEnabled as isSupabaseEnabled } from '../../config/supabaseClient'

// Acción asíncrona para login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', credentials)
      return response.data
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Error de conexión'
      return rejectWithValue(message)
    }
  }
)

// Acción asíncrona para logout
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/auth/logout')
      return true
    } catch (error) {
      // Aún si falla el logout en el servidor, limpiamos el cliente
      return true
    }
  }
)

// Hidrata Redux desde la sesión Clerk activa. Se dispara desde
// ClerkSessionSync una vez que el token Clerk está disponible para axios.
// El backend responde { user, tenant, clerk, tenantId }.
export const hydrateFromClerk = createAsyncThunk(
  'auth/hydrateFromClerk',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/clerk/me')
      return response.data
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Error al hidratar sesión Clerk'
      return rejectWithValue(message)
    }
  }
)

// Refresco SILENCIOSO de la sesión (mismo /clerk/me que hydrate, pero NO destructivo):
// actualiza user/permisos y tenant si llega bien; ante cualquier fallo NO desloguea ni toca
// el estado. Lo dispara Layout al montar para que cambios de permisos/cargo se reflejen sin
// re-login (cierra la trampa de permisos viejos en el JWT/sesión).
export const refreshSession = createAsyncThunk(
  'auth/refreshSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/clerk/me')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'refresh fallido')
    }
  }
)

const initialState = {
  user: null,
  token: localStorage.getItem('bomberosToken') || null,
  tenant: (() => { try { return JSON.parse(localStorage.getItem('bomberosTenant') || 'null'); } catch { return null; } })(),
  isAuthenticated: false,
  demo: false,
  loading: false,
  error: null,
  // Con auth externo (Clerk/Supabase), la sesión se resuelve async al cargar.
  // bootstrapping=true evita que ProtectedRoute expulse al usuario en el primer
  // render (antes de que el SessionSync hidrate). Los SessionSync lo apagan.
  bootstrapping: isClerkEnabled() || isSupabaseEnabled(),
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    // Sesión del tenant demo de la landing (token firmado por el server,
    // solo lectura). Hidrata auth completo para que TenantRoute/ProtectedRoute
    // pasen solos; state.demo activa el modo sandbox del client.
    demoSessionLoaded: (state, action) => {
      state.user = action.payload.user
      state.tenant = action.payload.tenant
      state.isAuthenticated = true
      state.loading = false
      state.bootstrapping = false
      state.demo = true
      state.error = null
    },
    // Marca que el chequeo inicial de sesión (Clerk/Supabase) ya terminó.
    authBootstrapped: (state) => {
      state.bootstrapping = false
    },
    // Limpia el estado de auth sin POST al backend.
    // Usado por ClerkSessionSync cuando la sesión Clerk termina (legacy
    // logoutUser POSTea a /auth/logout que devuelve 410 bajo Clerk).
    clearAuth: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.token = null
      state.tenant = null
      state.error = null
      state.loading = false
      state.bootstrapping = false
      localStorage.removeItem('bomberosToken')
      localStorage.removeItem('bomberosUser')
      localStorage.removeItem('bomberosTenant')
    },
    // Para verificar token existente al cargar la app
    checkAuthToken: (state) => {
      const token = localStorage.getItem('bomberosToken')
      const user = localStorage.getItem('bomberosUser')
      const tenant = localStorage.getItem('bomberosTenant')
      
      if (token && user) {
        try {
          state.token = token
          state.user = JSON.parse(user)
          state.tenant = tenant ? JSON.parse(tenant) : null
          state.isAuthenticated = true
        } catch (error) {
          // Si hay error al parsear, limpiar todo
          localStorage.removeItem('bomberosToken')
          localStorage.removeItem('bomberosUser')
          localStorage.removeItem('bomberosTenant')
          state.token = null
          state.user = null
          state.tenant = null
          state.isAuthenticated = false
        }
      } else {
        state.token = null
        state.user = null
        state.tenant = null
        state.isAuthenticated = false
      }
    },
  },
  extraReducers: (builder) => {
    // Login cases
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.user = action.payload.data.user
        state.token = action.payload.data.token
        state.tenant = action.payload.data.tenant || null
        state.error = null
        // Guardar en localStorage
        localStorage.setItem('bomberosToken', action.payload.data.token)
        localStorage.setItem('bomberosUser', JSON.stringify(action.payload.data.user))
        if (action.payload.data.tenant) {
          localStorage.setItem('bomberosTenant', JSON.stringify(action.payload.data.tenant))
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.tenant = null // sin esto quedaba el tenant del login anterior (fuga cross-tenant)
        state.error = action.payload
        // Limpiar localStorage
        localStorage.removeItem('bomberosToken')
        localStorage.removeItem('bomberosUser')
        localStorage.removeItem('bomberosTenant')
      })
      // Logout cases
      .addCase(logoutUser.fulfilled, (state) => {
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.tenant = null
        state.error = null
        state.loading = false
        // Limpiar localStorage
        localStorage.removeItem('bomberosToken')
        localStorage.removeItem('bomberosUser')
        localStorage.removeItem('bomberosTenant')
      })
      // Hidratación desde Clerk
      .addCase(hydrateFromClerk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(hydrateFromClerk.fulfilled, (state, action) => {
        state.loading = false
        state.bootstrapping = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.tenant = action.payload.tenant || null
        state.error = null
        // Persistir para que un reload conserve sesión (el token Clerk lo
        // refresca ClerkSessionSync; no se guarda en localStorage).
        if (action.payload.user) {
          localStorage.setItem('bomberosUser', JSON.stringify(action.payload.user))
        }
        if (action.payload.tenant) {
          localStorage.setItem('bomberosTenant', JSON.stringify(action.payload.tenant))
        }
      })
      .addCase(hydrateFromClerk.rejected, (state, action) => {
        state.loading = false
        state.bootstrapping = false
        state.isAuthenticated = false
        state.user = null
        state.tenant = null
        state.error = action.payload
      })
      // Refresco silencioso: sólo actualiza en éxito; en fallo NO toca el estado (no desloguea).
      .addCase(refreshSession.fulfilled, (state, action) => {
        if (!action.payload?.user) return
        state.user = action.payload.user
        if (action.payload.tenant) state.tenant = action.payload.tenant
        try {
          localStorage.setItem('bomberosUser', JSON.stringify(action.payload.user))
          if (action.payload.tenant) localStorage.setItem('bomberosTenant', JSON.stringify(action.payload.tenant))
        } catch { /* ignore */ }
      })
  },
})

export const {
  clearError,
  clearAuth,
  checkAuthToken,
  authBootstrapped,
  demoSessionLoaded,
} = authSlice.actions

export const selectUser = (state) => state.auth.user
export const selectTenant = (state) => state.auth.tenant

export default authSlice.reducer